import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';

const useStyles = makeStyles((theme: Theme) => ({
  pageContainer: {
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[100],
  },
  pageContent: {
    width: '100%',
    maxWidth: 1080,
    margin: theme.spacing(4),
    display: 'flex',
    borderRadius: theme.shape.borderRadius * 2,
    overflow: 'hidden',
    boxShadow:
      '0px 24px 48px rgba(17, 24, 39, 0.12), 0px 12px 24px rgba(17, 24, 39, 0.08)',
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      margin: theme.spacing(2),
    },
  },
  leftPanel: {
    flex: 1,
    minHeight: 480,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
    background:
      'linear-gradient(135deg, #2647FF 0%, #0F1C45 50%, #3F86FF 100%)',
    color: theme.palette.common.white,
    [theme.breakpoints.down('md')]: {
      minHeight: 320,
      padding: theme.spacing(4),
    },
  },
  rightPanel: {
    flex: 1,
    padding: theme.spacing(6),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(4),
    },
  },
  logoBanner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    padding: theme.spacing(1.25, 4),
    borderRadius: 999,
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: 700,
    fontSize: 24,
    width: '100%',
    maxWidth: 360,
  },
}));

const LoginPage: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const success = await login(username, password);

      if (success) {
        navigate('/', { replace: true });
        return;
      }

      setError('Invalid credentials. Please try again.');
    } catch (err) {
      setError('Login failed. Please check your connection and try again.');
      console.error('Login error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className={classes.pageContainer}>
      <Box className={classes.pageContent}>
        <Box className={classes.leftPanel}>
          <Box className={classes.logoBanner}>Conexio Support</Box>
        </Box>

        <Box className={classes.rightPanel}>
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5 }}>
              Sign in
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 4 }}
            >
              Enter the support credentials to continue.
            </Typography>

            <TextField
              label="Username"
              type="text"
              fullWidth
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              margin="normal"
              required
              autoComplete="username"
            />

            <TextField
              label="Password"
              fullWidth
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              margin="normal"
              required
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="large"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error ? (
              <Typography
                variant="body2"
                sx={{ color: 'error.main', mt: 2, fontWeight: 500 }}
              >
                {error}
              </Typography>
            ) : null}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              sx={{
                mt: 4,
                py: 1.5,
                textTransform: 'none',
                fontSize: 16,
                fontWeight: 600,
              }}
              disabled={!username || !password || isSubmitting}
            >
              {isSubmitting ? 'Signing in...' : 'Access Dashboard'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
